import { ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { StatusBadge } from "@/components/status-badge"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { IntakeLoad, PendingArrival } from "@/features/intake/api/types"
import {
  decisionTone,
  stageTone,
} from "@/features/intake/lib/intake-status"
import { certificateTranslationKey } from "@/lib/certificates"
import { formatNumber, kgToMt } from "@/lib/format"
import { paths } from "@/routes/paths"

const headCellClass =
  "h-11 px-4 text-muted-foreground text-xs font-medium uppercase tracking-wide"
const bodyCellClass = "px-4 py-4"

type IntakeRowItem =
  | { kind: "pending"; pending: PendingArrival }
  | { kind: "load"; load: IntakeLoad }

interface IntakeTableProps {
  rows: IntakeRowItem[]
  onSelectPending: (pending: PendingArrival) => void
}

export function IntakeTable({ rows, onSelectPending }: IntakeTableProps) {
  const { t } = useTranslation("intake")
  const navigate = useNavigate()

  return (
    <Card className="overflow-hidden p-0">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead className={headCellClass}>
                {t("row.cooperative")}
              </TableHead>
              <TableHead className={headCellClass}>
                {t("row.deliverySlip")}
              </TableHead>
              <TableHead className={headCellClass}>{t("row.plate")}</TableHead>
              <TableHead className={`${headCellClass} text-right`}>
                {t("row.mt")}
              </TableHead>
              <TableHead className={headCellClass}>
                {t("row.client")}
              </TableHead>
              <TableHead className={headCellClass}>
                {t("row.certifications")}
              </TableHead>
              <TableHead className={`${headCellClass} min-w-[120px]`}>
                {t("row.status")}
              </TableHead>
              <TableHead aria-hidden className={`${headCellClass} w-8`} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((item) => {
              if (item.kind === "pending") {
                const p = item.pending
                const cooperative = p.orderId?.groupId?.name ?? "—"
                const slip = p.billOfLadingNumber ?? "—"
                const kg = p.netKilogramsAttached ?? null
                const client =
                  p.orderId?.exporterOrderId?.clientName ?? null
                const certificates = p.orderId?.certificates ?? []
                return (
                  <TableRow
                    key={`p-${p.id}`}
                    className="hover:bg-muted/60 cursor-pointer"
                    onClick={() => onSelectPending(p)}
                  >
                    <TableCell className={`${bodyCellClass} font-medium`}>
                      {cooperative}
                    </TableCell>
                    <TableCell
                      className={`${bodyCellClass} font-mono tabular-nums`}
                    >
                      {slip}
                    </TableCell>
                    <TableCell className={bodyCellClass}>
                      {p.truckRegistrationNumber ? (
                        <span className="font-mono tabular-nums">
                          {p.truckRegistrationNumber}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/70 italic text-xs">
                          {t("row.noPlate")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell
                      className={`${bodyCellClass} text-right tabular-nums`}
                    >
                      {kg != null ? `${formatNumber(kgToMt(kg), 1)} MT` : "—"}
                    </TableCell>
                    <TableCell className={bodyCellClass}>
                      {client ?? (
                        <span className="text-muted-foreground/70">—</span>
                      )}
                    </TableCell>
                    <TableCell className={bodyCellClass}>
                      <CertificationCell certificates={certificates} />
                    </TableCell>
                    <TableCell className={bodyCellClass}>
                      <StatusBadge tone={stageTone("PENDING")}>
                        {t("status.PENDING")}
                      </StatusBadge>
                    </TableCell>
                    <TableCell className={bodyCellClass}>
                      <ChevronRight className="text-muted-foreground size-4" />
                    </TableCell>
                  </TableRow>
                )
              }

              const load = item.load
              const slip = load.DeliveryForms?.billOfLadingNumber ?? "—"
              const cooperative = load.Orders?.groupId?.name ?? "—"
              const kg =
                load.netWeight ??
                load.DeliveryForms?.netKilogramsAttached ??
                null
              const client = load.ExporterOrders?.clientName ?? null
              const certificates = load.Orders?.certificates ?? []
              const badgeLabel = load.decision
                ? t(`loadDecision.${load.decision}`)
                : t(`status.${load.status}`)
              const badgeTone = load.decision
                ? decisionTone(load.decision)
                : stageTone(load.status)

              return (
                <TableRow
                  key={`l-${load.id}`}
                  className="hover:bg-muted/60 cursor-pointer"
                  onClick={() => navigate(paths.receptionDetail(load.id))}
                >
                  <TableCell className={`${bodyCellClass} font-medium`}>
                    {cooperative}
                  </TableCell>
                  <TableCell
                    className={`${bodyCellClass} font-mono tabular-nums`}
                  >
                    {slip}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    {(() => {
                      const plate =
                        load.plateNumber ??
                        load.DeliveryForms?.truckRegistrationNumber ??
                        null
                      return plate ? (
                        <span className="font-mono tabular-nums">{plate}</span>
                      ) : (
                        <span className="text-muted-foreground/70 italic text-xs">
                          {t("row.noPlate")}
                        </span>
                      )
                    })()}
                  </TableCell>
                  <TableCell
                    className={`${bodyCellClass} text-right tabular-nums`}
                  >
                    {kg != null ? `${kgToMt(kg).toFixed(1)} MT` : "—"}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    {client ?? (
                      <span className="text-muted-foreground/70">—</span>
                    )}
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <CertificationCell certificates={certificates} />
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <StatusBadge tone={badgeTone}>{badgeLabel}</StatusBadge>
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <ChevronRight className="text-muted-foreground size-4" />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  )
}

interface CertificationCellProps {
  certificates: string[]
}

function CertificationCell({ certificates }: CertificationCellProps) {
  const { t } = useTranslation("intake")
  if (certificates.length === 0) {
    return <span className="text-muted-foreground/70">—</span>
  }
  const visible = certificates.slice(0, 2)
  const overflow = certificates.length - visible.length
  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((cert) => {
        const key = certificateTranslationKey(cert)
        const label = key ? t(key) : cert
        return (
          <Badge
            key={cert}
            variant="secondary"
            className="text-[11px] font-normal"
          >
            {label}
          </Badge>
        )
      })}
      {overflow > 0 ? (
        <span className="text-muted-foreground text-[11px] tabular-nums">
          +{overflow}
        </span>
      ) : null}
    </div>
  )
}
